import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
const apiUrl = process.env.REACT_APP_API_URL;

function AdminBatchLessons() {
  const { batchid } = useParams();
  const [lessons, setLessons] = useState([]);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [lesson_date, setLessonDate] = useState('');
  const [youtube_link, setYoutubeLink] = useState('');
  const [files, setFiles] = useState([]); // Array of {title, file}
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Memoize fetchLessons with useCallback
  const fetchLessons = useCallback(async () => {
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setMessage('You must be logged in to view lessons.');
        setMessageType('error');
        setLoading(false);
        return;
      }
      console.log(`Fetching lessons for batchid: ${batchid} (Type: ${typeof batchid})`); // Debug log
      const response = await axios.get(`${apiUrl}/api/admin/batch-lessons/${batchid}`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      console.log('API Response:', response.data); // Debug log
      if (response.data.success) {
        setLessons(response.data.lessons || []); // Ensure lessons is an array
      } else {
        setMessage(response.data.message || 'Failed to fetch lessons.');
        setMessageType('error');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching lessons.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  }, [batchid]); // Dependency on batchid

  // Use useEffect with memoized fetchLessons
  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]); // Use memoized fetchLessons

  const addFileField = () => {
    setFiles([...files, { title: '', file: null }]);
  };

  const removeFileField = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
  };

  const handleFileChange = (index, e) => {
    const updatedFiles = [...files];
    updatedFiles[index].file = e.target.files[0];
    setFiles(updatedFiles);
  };

  const handleFileTitleChange = (index, e) => {
    const updatedFiles = [...files];
    updatedFiles[index].title = e.target.value;
    setFiles(updatedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');
    const formData = new FormData();
    formData.append('batchid', batchid);
    formData.append('title', title);
    formData.append('summary', summary);
    formData.append('lesson_date', lesson_date);
    formData.append('youtube_link', youtube_link);
    formData.append('fileTitles', JSON.stringify(files.map(f => f.title)));
    files.forEach((f) => {
      if (f.file) formData.append('files', f.file);
    });
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setMessage('You must be logged in to add a lesson.');
        setMessageType('error');
        setLoading(false);
        return;
      }
      const response = await axios.post(`${apiUrl}/api/admin/lessons`, formData, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setMessage('Lesson added successfully!');
        setMessageType('success');
        setTitle('');
        setSummary('');
        setLessonDate('');
        setYoutubeLink('');
        setFiles([]);
        fetchLessons(); // Refresh lessons list
      } else {
        setMessage(response.data.message || 'Failed to add lesson.');
        setMessageType('error');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error adding lesson.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <Link to="/admin-view-batches" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    View Batches
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Batch Lessons</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="card overflow-hidden">
            <div className="card-body p-24">
              <h4 className="mb-16">Add New Lesson</h4>
              {message && (
                <div className={`alert alert-${messageType} mb-16`}>
                  {message}
                </div>
              )}
              {error && (
                <div className="alert alert-danger mb-16">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="mb-24">
                <div className="mb-3">
                  <label htmlFor="title" className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="summary" className="form-label">Summary</label>
                  <textarea
                    className="form-control"
                    id="summary"
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="lesson_date" className="form-label">Lesson Date</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    id="lesson_date"
                    value={lesson_date}
                    onChange={(e) => setLessonDate(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="youtube_link" className="form-label">YouTube Link</label>
                  <input
                    type="url"
                    className="form-control"
                    id="youtube_link"
                    value={youtube_link}
                    onChange={(e) => setYoutubeLink(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Files</label>
                  <button type="button" className="btn btn-primary" onClick={addFileField}>
                    Add File
                  </button>
                  {files.map((file, index) => (
                    <div key={index} className="d-flex align-items-end gap-2 mt-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="File Title"
                        value={file.title}
                        onChange={(e) => handleFileTitleChange(index, e)}
                        required
                      />
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) => handleFileChange(index, e)}
                        required
                      />
                      <button type="button" className="btn btn-danger" onClick={() => removeFileField(index)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Adding...' : 'Add Lesson'}
                </button>
              </form>

              <h4 className="mb-16">Lesson List</h4>
              {loading ? (
                <div className="alert alert-info">Loading lessons...</div>
              ) : error ? (
                <div className="alert alert-danger">{error}</div>
              ) : lessons.length === 0 ? (
                <div className="alert alert-warning">No lessons found.</div>
              ) : (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Date</th>
                      <th>Summary</th>
                      <th>YouTube Link</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lessons.map((lesson) => (
                      <tr key={lesson.id}>
                        <td>{lesson.title}</td>
                        <td>{new Date(lesson.lesson_date).toLocaleString()}</td>
                        <td>{lesson.summary || 'N/A'}</td>
                        <td>{lesson.youtube_link || 'N/A'}</td>
                        <td>
                          {/* Add edit/delete if needed */}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
export default AdminBatchLessons;